import agent from '../../../services/api';
import { generateUUID } from '../../../utils/constants';
import { chatbotIntegratedChannelDeploymentObj, customVariableDataInterface, staticWorkflowChatbotDetails } from '../../../models/axiosModel';
import { saveDeploymentInformationPayload } from '../../../models/flowBuilderTypes';

type AnyObject = Record<string, any>;

export type ImportChatbotVersion = "Draft" | "Published";

export interface ImportPayloadResult {
    payload: { 
        source_chatbot_id: string, 
        payload: AnyObject[] 
    } | null,
    errors: string[],
}

// Keys whose id is replaced with a new uuid for the destination (step, flow, action and nested row ids).
// Foreign keys (templates, tags, lists, ...) are kept as is, so `action_type_id` and `action_contact_list_id` are excluded from `action_*_id`.
const NEW_ID_KEY = /^(chatbot_step_id|parent_chatbot_step_id|chatbot_step_flow_id|chatbot_step_action_id|chatbot_step_exception_id|action_(?!type_id$|contact_list_id$).+_id|unique_id)$/;
const VARIABLE_ID_KEYS = new Set(["chatbot_custom_variable_configuration_id", "rhs_chatbot_custom_variable_configuration_id", "reset_custom_variable_id"]);

const isPlainObject = (value: unknown): value is AnyObject => !!value && typeof value === "object" && !Array.isArray(value);


/* ---------------------------------- Deployment ---------------------------------- */

// WABA and phone number of a chatbot (first deployed channel)
const getWhatsAppDeployment = (chatbotDetail: staticWorkflowChatbotDetails | null | undefined) => {
    const channel = chatbotDetail?.chatbot_integrated_channel_deployment?.integrated_channel_data?.[0] as chatbotIntegratedChannelDeploymentObj | undefined;
    return {
        metaIntegrationWhatsAppId: channel?.meta_integration_whatsapp_id,
        integratedChannelId: channel?.meta_integration_whatsapp_phone_number_data?.[0]?.integrated_channel_id,
    };
}

// Payload to complete the destination chatbot's deployment (same payload the deployment settings save sends): the WABA / phone number it already has are kept,
// the missing one is taken from the source chatbot. Triggers and timeout config stay as they are. Returns null if the deployment can't be completed.
export const createDeploymentPayload = (destChatbotDetail: staticWorkflowChatbotDetails, sourceChatbotDetail: staticWorkflowChatbotDetails): saveDeploymentInformationPayload | null => {

    const dest = getWhatsAppDeployment(destChatbotDetail);
    const source = getWhatsAppDeployment(sourceChatbotDetail);

    const metaIntegrationWhatsAppId = dest.metaIntegrationWhatsAppId || source.metaIntegrationWhatsAppId;
    const integratedChannelId = dest.integratedChannelId || source.integratedChannelId;
    if (!metaIntegrationWhatsAppId || !integratedChannelId) return null;

    const getContentTypeId = (detail: staticWorkflowChatbotDetails) => Number(detail?.chatbot_integrated_channel_deployment?.deployment_channel_content_type_data?.channel_content_type_id) || null;

    return {
        chatbot_id: destChatbotDetail.chatbot_id,
        chatbot_name: destChatbotDetail.chatbot_name,
        chatbot_channel_configuration: {
            meta_integration_whatsapp_id: metaIntegrationWhatsAppId,
            integrated_channel_id: String(integratedChannelId),
            channel_content_type_id: getContentTypeId(destChatbotDetail) ?? getContentTypeId(sourceChatbotDetail),
        },
        chatbot_step_deployment_trigger_data: (getSourceVersion(destChatbotDetail, "Draft")?.chatbot_step_deployment_trigger_data ?? []).map((trigger: AnyObject) => ({
            ...trigger,
            chatbot_step_deployment_trigger_data_id: trigger.chatbot_step_deployment_trigger_data_id,
            condition_group: trigger.condition_group ? {
                condition_type: trigger.condition_group.condition_type,
                condition_values: trigger.condition_group.condition_values,
                flag_advanced_condition: trigger.condition_group.flag_advanced_condition,
            } : null,
        })),
        chatbot_thread_timeout_config: destChatbotDetail.chatbot_thread_timeout_config,
    };
}


/* ---------------------------------- Variables ---------------------------------- */

const fetchCustomVariables = async (chatbotId: string) => {
    const res = await agent.getChatbotCustomVariable.get(chatbotId);
    return { 
        res, 
        variables: (res.status ? res.data?.custom_variable_data ?? [] : []) as customVariableDataInterface[] 
    };
}

// Variable names are compared trimmed and case insensitive
const getVariableName = (variable: customVariableDataInterface) => (variable?.chatbot_custom_variable_name ?? "").trim().toLowerCase();

// Source variables whose name doesn't exist in the destination yet. Archived ones are included too, as steps can still reference them.
export const getMissingVariables = (sourceVariables: customVariableDataInterface[], destVariables: customVariableDataInterface[]) => {
    
    const existingNames = new Set(destVariables.map(getVariableName));
    
    return sourceVariables.filter((variable) => {
        const name = getVariableName(variable);
        if (existingNames.has(name)) {
            return false;
        }

        existingNames.add(name); // also dedupes repeated source names
        return true;
    });
}

// Src variable id -> Dest variable id, matched by name (trimmed, case insensitive)
export const createVariableIdMap = (sourceVariables: customVariableDataInterface[], destVariables: customVariableDataInterface[]) => {
    const destIdByName = new Map(destVariables.map((variable) => [getVariableName(variable), variable.chatbot_custom_variable_configuration_id]));
    const variableIdMap = new Map<string, string>();

    sourceVariables.forEach((variable) => {
        const destId = destIdByName.get(getVariableName(variable));
        if (destId) {
            variableIdMap.set(variable.chatbot_custom_variable_configuration_id, destId);
        }
    });
    
    return variableIdMap;
}

// Creates the source variables missing in the destination, then returns the Src -> Dest id map. `error` holds the failed response.
export const importCustomVariables = async (sourceChatbotId: string, destChatbotId: string) => {
    
    const [source, dest] = await Promise.all([fetchCustomVariables(sourceChatbotId), fetchCustomVariables(destChatbotId)]);
    const failed = [source.res, dest.res].find((res) => !res.status);
    
    if (failed) return { 
        error: failed, 
        variableIdMap: new Map<string, string>() 
    };

    const missingVariables = getMissingVariables(source.variables, dest.variables);
    let destVariables = dest.variables;

    if (missingVariables.length) {
        const variablesPayload = {
            chatbot_id: destChatbotId,
            custom_variables: missingVariables.map((variable) => ({
                chatbot_custom_variable_name: variable.chatbot_custom_variable_name,
                chatbot_custom_variable_fallback_value: variable.chatbot_custom_variable_fallback_value ?? variable.chatbot_custom_variable_name ?? "",
            })),
        }

        const res = await agent.addChatbotCustomVariable.put(variablesPayload);
        if (!res.status) return { 
            error: res, 
            variableIdMap: new Map<string, string>() 
        };

        // The response has the new variables (with ids); merged with the existing ones in case it doesn't return the full list. Re-fetch only if it has no list.
        const created = res.data?.["custom-variable-data"] ?? res.data?.custom_variable_data;
        
        if (created) {
            destVariables = [...dest.variables, ...created];
        } else {
            const latest = await fetchCustomVariables(destChatbotId);
            if (!latest.res.status) return { 
                error: latest.res, 
                variableIdMap: new Map<string, string>() 
            };
            destVariables = latest.variables;
        }
    }

    return { 
        error: null, 
        variableIdMap: createVariableIdMap(source.variables, destVariables) 
    };
}


/* ------------------------------------ IDs ------------------------------------ */

// Memoised Src id -> Dest uuid, so every reference to the same source id (step, flow, action, condition unique_id) gets the same dest id
const createIdContext = (variableIdMap: Map<string, string>) => {

    const destIdBySrcId = new Map<string, string>();
    const unresolvedVariableIds = new Set<string>();

    // Returns: new uuid for a source id (same uuid on every call, so steps and their flows / actions stay linked). Used in: remapIds (all NEW_ID_KEY keys) and the step ids of the node, action and edge activities
    const getDestId = (srcId: string) => {
        if (!destIdBySrcId.has(srcId)) {
            destIdBySrcId.set(srcId, generateUUID());
        }
        return destIdBySrcId.get(srcId) as string;
    };

    // Returns: id of the same-named variable in the destination chatbot; an unmatched id is kept and recorded so the import is blocked. Used in: remapIds (variable id keys in actions, intents and flow conditions)
    const getDestVariableId = (srcId: string) => {
        const destId = variableIdMap.get(srcId);
        if (!destId) {
            unresolvedVariableIds.add(srcId);
        }
        return destId ?? srcId;
    };

    return {
        unresolvedVariableIds,
        getDestId,
        getDestVariableId,
    };
}
type IdContext = ReturnType<typeof createIdContext>;

// Deep copy of `value` with every id remapped to its destination id
const remapIds = (value: any, ctx: IdContext): any => {
    
    if (Array.isArray(value)) {
        return value.map((item) => remapIds(item, ctx));
    }
    if (!isPlainObject(value)) {
        return value;
    }

    return Object.fromEntries(
        Object.entries(value).map(([key, val]) => {
            if (typeof val === "string" && val) {
                if (NEW_ID_KEY.test(key)) {
                    return [key, ctx.getDestId(val)];
                }
                if (VARIABLE_ID_KEYS.has(key)) {
                    return [key, ctx.getDestVariableId(val)];
                }
            }
            return [key, remapIds(val, ctx)];
        })
    );
}


/* ------------------------------ Nodes / Actions / Flows ------------------------------ */

// Same check the canvas uses to hide the Redeploy step
export const isRedeployStep = (step: AnyObject) => step?.chatbot_step_type?.chatbot_step_type_name?.toLowerCase() === "redeploy";

// Draft may not exist for a published-only chatbot, in which case it is a copy of the published version
export const getSourceVersion = (chatbotDetails: AnyObject | null | undefined, version: ImportChatbotVersion) => {
    const versions: AnyObject[] = chatbotDetails?.chatbot_version_info ?? [];
    const findVersion = (name: string) => versions.find((item) => item?.chatbot_version_status_name?.toLowerCase() === name);
    return findVersion(version.toLowerCase()) ?? (version === "Draft" ? findVersion("published") : undefined);
}


// Intent: same shape the canvas sends on save (variable ids inside are already remapped)
const createIntent = (intent: AnyObject) => ({
    intent_id: intent?.intent_id,
    chatbot_custom_variable_configuration_id: intent?.chatbot_custom_variable_configuration_id,
    condition_group: (intent?.condition_group?.custom_variable_conditions?.chatbot_custom_variable_configuration_id) ? intent.condition_group : null,
    flag_duplicate: intent?.flag_duplicate,
});

// Action: the source action with remapped ids, in the shape sent on save.
// The source action already has every field, so no defaults are needed.
const createAction = (srcAction: AnyObject, ctx: IdContext) => {

    const action = remapIds(srcAction, ctx);
    const intents: AnyObject[] = action.intents ?? action.intent ?? [];
    delete action.intents;

    return { ...action, intent: intents.map(createIntent) };
}

// Flow (edge): ignores flows coming from steps that aren't imported; condition group gets a fresh id like a new edge
const createStepFlows = (flows: AnyObject[] | null | undefined, importedStepIds: Set<string>, destChatbotId: string, ctx: IdContext) => {
    return (flows ?? [])
        .filter((flow) => !flow?.parent_chatbot_step_id || importedStepIds.has(flow.parent_chatbot_step_id))
        .map((flow) => {
            const mapped = remapIds(flow, ctx);
            return {
                ...mapped,
                chatbot_id: destChatbotId,
                condition_group: mapped.condition_group ? { ...mapped.condition_group, condition_group_id: "" } : null,
            };
        });
}

// Node: new step id, name, position and type
const createNodeActivity = (srcStep: AnyObject, ctx: IdContext) => ({
    activity_type: "update_coordinates",
    chatbot_step_id: ctx.getDestId(srcStep.chatbot_step_id),
    flag_start_node: srcStep.flag_start_node,
    chatbot_step_name: srcStep.chatbot_step_name,
    coordinates: srcStep.coordinates,
    chatbot_step_type_id: srcStep.chatbot_step_type?.chatbot_step_type_id ?? null,
});

// Node actions
const createNodeActionsActivity = (srcStep: AnyObject, ctx: IdContext) => ({
    activity_type: "update_step",
    chatbot_step_id: ctx.getDestId(srcStep.chatbot_step_id),
    chatbot_step_action: (srcStep.chatbot_step_action_data ?? []).map((action: AnyObject) => createAction(action, ctx)),
    chatbot_step_type_id: srcStep.chatbot_step_type?.chatbot_step_type_id ?? null,
});

// Error action of the step (same payload the canvas sends): the target step is another imported step (Redirect) or empty (Do Nothing).
// A target that isn't imported is left empty.
const createExceptionActivity = (srcStep: AnyObject, importedStepIds: Set<string>, ctx: IdContext) => {

    const exception = srcStep.chatbot_step_exception;
    const targetStepId = exception.target_chatbot_step?.target_chatbot_step_id;

    return {
        activity_type: "update_exception",
        chatbot_step_id: ctx.getDestId(srcStep.chatbot_step_id),
        chatbot_step_type_id: srcStep.chatbot_step_type?.chatbot_step_type_id ?? null,
        chatbot_exception: {
            chatbot_step_exception_id: ctx.getDestId(exception.chatbot_step_exception_id),
            target_chatbot_step_id: importedStepIds.has(targetStepId) ? ctx.getDestId(targetStepId) : "",
            exception_message: exception.exception_message,
            flag_delete: false,
        },
    };
}

// Edges pointing to the node
const createEdgesActivity = (srcStep: AnyObject, importedStepIds: Set<string>, destChatbotId: string, ctx: IdContext) => ({
    activity_type: "update_step_flow",
    chatbot_step_id: ctx.getDestId(srcStep.chatbot_step_id),
    chatbot_step_flow: createStepFlows(srcStep.chatbot_step_flow, importedStepIds, destChatbotId, ctx),
});


/* ------------------------------------ Payload ------------------------------------ */

interface CreateImportPayloadParams {
    sourceChatbotDetail: AnyObject | null,
    version: ImportChatbotVersion,
    destChatbotId: string,
    variableIdMap: Map<string, string>,
}

// Payload flow:
// selected version -> skip Redeploy steps
//   -> nodes: update_coordinates (new step id, position, type) -> update_step (new action ids, variable ids via Src -> Dest map)
//   -> error actions: update_exception (new exception id, new target step id)
//   -> edges: update_step_flow (new flow id, new step / parent step ids)
//   -> validate unmatched variables -> { source_chatbot_id, payload }

export const createImportRuleBasedChatbotPayload = ({ sourceChatbotDetail, version, destChatbotId, variableIdMap }: CreateImportPayloadParams): ImportPayloadResult => {
    
    const sourceVersion = getSourceVersion(sourceChatbotDetail, version);
    if (!sourceVersion) {
        return { 
            payload: null, 
            errors: [`${version} version of the chatbot was not found.`] 
        };
    }

    const sourceSteps: AnyObject[] = (sourceVersion.chatbot_step_data ?? []).filter((step: AnyObject) => !isRedeployStep(step));
    if (!sourceSteps.length) {
        return { 
            payload: null, 
            errors: ["The selected chatbot has no steps to import."] 
        };
    }

    const importedStepIds = new Set(sourceSteps.map((step) => step.chatbot_step_id));
    const ctx = createIdContext(variableIdMap);

    // 1. Nodes, 2. Node actions, 3. Node error actions, 4. then edges
    const nodes = sourceSteps.map((step) => createNodeActivity(step, ctx));
    const nodeActions = sourceSteps.map((step) => createNodeActionsActivity(step, ctx));
    const nodeExceptions = sourceSteps.filter((step) => step.chatbot_step_exception).map((step) => createExceptionActivity(step, importedStepIds, ctx));
    const edges = sourceSteps.map((step) => createEdgesActivity(step, importedStepIds, destChatbotId, ctx)).filter((edge) => edge.chatbot_step_flow.length);

    if (ctx.unresolvedVariableIds.size) {
        return { 
            payload: null, 
            errors: [`Some variables used in the chatbot could not be matched in this chatbot (${ctx.unresolvedVariableIds.size}).`] 
        };
    }

    return { 
        payload: { 
            source_chatbot_id: sourceChatbotDetail?.chatbot_id, 
            payload: [...nodes, ...nodeActions, ...nodeExceptions, ...edges]
        }, 
        errors: [] 
    };
}
